import { Component, inject, OnInit } from '@angular/core';
import { CalldiusStepItem, CallidusStepperComponent } from '@shared/components/callidus-stepper/callidus-stepper.component';
import { CustomerDataComponent } from '@shared/components/customer-data/customer-data.component';
import { DataSubmittedComponent } from '@shared/components/data-submitted/data-submitted.component';
import { FormGroup } from '@angular/forms';
import { unsubscribeMixin } from '@shared/mixin/unsubscribe.mixin';
import { createSkipperForm } from '@shared/factories/skipper-form.factory';
import { SkipperFormModel } from '@shared/models/forms/skipper-form.model';
import { Router } from '@angular/router';
import { SkipperGeneralComponent } from '../../components/skipper-general/skipper-general.component';
import { takeUntil } from 'rxjs';
import { SkipperHaftpflichtDetailsComponent } from '../../components/skipper-haftpflicht-details/skipper-haftpflicht-details.component';
import { SkipperRechtsschutzDetailsComponent } from '../../components/skipper-rechtsschutz-details/skipper-rechtsschutz-details.component';
import { SkipperStateService } from '@shared/services/skipper-state.service';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';
import { BrokernetLanguageType, CallidusSkipperVersicherungspraemieCalcDto, PublicAddressCreateDto, PublicCallidusSkipperContractCreateDto, PublicPersonCreateDto } from '@backend/models';

@Component({
  selector: 'app-skipper-haftpflicht-page',
  imports: [
    CallidusStepperComponent,
    CustomerDataComponent,
    DataSubmittedComponent,
    SkipperGeneralComponent,
    SkipperHaftpflichtDetailsComponent,
    SkipperRechtsschutzDetailsComponent
  ],
  templateUrl: './skipper-page.component.html',
  styleUrl: './skipper-page.component.scss'
})
export class SkipperPageComponent extends unsubscribeMixin() implements OnInit {
  private readonly skipperStateService = inject(SkipperStateService);
  private readonly router = inject(Router);
  private readonly urlParamProviderService = inject(UrlParamProviderService);

  protected readonly SkipperHaftpflichtNavigationEnum = SkipperHaftpflichtNavigationEnum;

  stepItems: CalldiusStepItem[] = [
    {
      id: SkipperHaftpflichtNavigationEnum.Base.toString(),
      label: 'bvs.calculator.menu.infos',
      iconUrl: '/assets/icons/info-circle.svg',
      enabled: true
    },
    {
      id: SkipperHaftpflichtNavigationEnum.SkipperHaftpflichtDetails.toString(),
      label: 'bvs.calculator.menu.haftpflichtDetails',
      iconUrl: '/assets/icons/info-circle.svg',
      enabled: false
    },
    {
      id: SkipperHaftpflichtNavigationEnum.SkipperRechtsschutzDetails.toString(),
      label: 'bvs.calculator.menu.rechtsschutzDetails',
      iconUrl: '/assets/icons/info-circle.svg',
      enabled: false
    },
    {
      id: SkipperHaftpflichtNavigationEnum.Customer.toString(),
      label: 'bvs.calculator.menu.finish',
      iconUrl: '/assets/icons/health-shield.svg',
      enabled: true
    }
  ];

  curentNavigationStep: SkipperHaftpflichtNavigationEnum = SkipperHaftpflichtNavigationEnum.Base;
  form!: FormGroup<SkipperFormModel>;

  private initialStepOrder: SkipperHaftpflichtNavigationEnum[] = [
    SkipperHaftpflichtNavigationEnum.Base,
    SkipperHaftpflichtNavigationEnum.SkipperHaftpflichtDetails,
    SkipperHaftpflichtNavigationEnum.SkipperRechtsschutzDetails,
    SkipperHaftpflichtNavigationEnum.Customer,
    SkipperHaftpflichtNavigationEnum.Done
  ];

  stepOrder = this.initialStepOrder;

  ngOnInit(): void {
    this.initializeForm();
    this.form.controls.isKundeSkipperVertragsRechtsschutzSelected.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.updateStepEnabledState(SkipperHaftpflichtNavigationEnum.SkipperRechtsschutzDetails, value!);
    });
    this.form.controls.isKundeSkipperVertragsHaftpflichtSelected.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.updateStepEnabledState(SkipperHaftpflichtNavigationEnum.SkipperHaftpflichtDetails, value!);
    });
  }

  updateStepEnabledState(stepEnum: SkipperHaftpflichtNavigationEnum, enabled: boolean): void {
    const step = this.stepItems.find(item => item.id === stepEnum.toString());
    if (step) {
      step.enabled = enabled;
    }
    this.updateStepOrder();
  }

  updateStepOrder(): void {
    const steps = this.stepItems
      .filter(item => item.enabled)
      .map(item => {
        return Number(item.id);
      });
    steps.push(SkipperHaftpflichtNavigationEnum.Done);
    this.stepOrder = steps;
  }

  getCurrentPosition(): number {
    return this.stepOrder.indexOf(this.curentNavigationStep);
  }

  moveToNextStep(): void {
    this.curentNavigationStep++;
    if (this.isCurrentStepDisabled()) {
      this.moveToNextStep();
    }
  }

  isCurrentStepDisabled(): boolean {
    const currentStep = this.stepItems.find(item => item.id === this.curentNavigationStep.toString());
    return currentStep ? !currentStep.enabled : true;
  }

  moveToPreviousStep(): void {
    if (this.curentNavigationStep === SkipperHaftpflichtNavigationEnum.Base) {
      void this.router.navigate(['/']);
    }

    this.curentNavigationStep--;
    if (this.isCurrentStepDisabled()) {
      this.moveToPreviousStep();
    }
  }

  submitData(): void {
    const controls = this.form.controls;
    const personData = this.form.controls.customerData.controls.person.getRawValue();
    const legalChecks = this.form.controls.customerData.controls.legalChecks.controls;
    const skipperHaftpflicht = this.form.controls.skipperHaftpflicht.controls;
    const skipperRechtsschutz = this.form.controls.skipperRechtsschutz.controls;
    const hasHaftpflicht = controls.isKundeSkipperVertragsHaftpflichtSelected.value ?? false;
    const hasRechtsschutz = controls.isKundeSkipperVertragsRechtsschutzSelected.value ?? false;

    const query = this.urlParamProviderService.getQueryData();
    const isLinked = !!query?.customerId && !!query?.personId;

    const onboardingCreateDto: PublicCallidusSkipperContractCreateDto = {
      callidusSkipperVersicherungspraemieCalcDto: {
        callidusSkipperVersicherungssummeKunde: hasHaftpflicht ? skipperHaftpflicht.callidusSkipperVersicherungssummeKunde.value : undefined,
        hasSchaedenBereitsErlitten: hasHaftpflicht ? skipperHaftpflicht.hasSchaedenBereitsErlitten.value : undefined,
        hasRisikenBereitsVersichert: hasHaftpflicht ? skipperHaftpflicht.hasRisikenBereitsVersichert.value : undefined,
        hasBeantragteRisikenGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.hasBeantragteRisikenGekuendigtOderAbgelehnt.value : undefined,
        isSelbstbehaltSelected: hasRechtsschutz ? skipperRechtsschutz.isSelbstbehaltSelected.value : undefined,
        isKundeAdditionalCharterVertragsRechtsschutzSelected: hasRechtsschutz ? skipperRechtsschutz.isKundeAdditionalCharterVertragsRechtsschutzSelected.value : undefined
      } as CallidusSkipperVersicherungspraemieCalcDto,

      publicLegalCheck: {
        hatElektronischeKommunikationAkzeptiert: legalChecks.isContactConsent.value ?? false,
        hatDsgvoAkzeptiert: legalChecks.isGdprAccepted.value ?? false,
        hatAvbAkzeptiert: false,
        hatIpidAkzeptiert: false,
        hatSepaEinzugAkzeptiert: false,
        hatWiderrufsrechtAkzeptiert: false,
        hatWuenscheUndBeduerfnisseAkzeptiert: false
      },

      ...(isLinked
        ? { publicLinkCustomerDto: { customerId: query!.customerId!, personId: query!.personId! } }
        : {
          publicPerson: {
            titlePrefix: personData.titlePrefix,
            titleSuffix: personData.titleSuffix,
            firstname: personData.firstname,
            lastname: personData.lastname,
            birthdate: personData.birthdate,
            gender: personData.gender,
            brokernetLanguageType: localStorage.getItem('app-language') === BrokernetLanguageType.En ? BrokernetLanguageType.En : BrokernetLanguageType.De,
            publicContactInfoCreateDto: {
              email: personData.email,
              phone: personData.phone
            },
            publicAddressCreateDto: controls.customerData.controls.address.getRawValue() as PublicAddressCreateDto
          } as PublicPersonCreateDto
        }),

      isKundeSkipperVertragsHaftpflichtSelected: hasHaftpflicht,
      isKundeSkipperVertragsRechtsschutzSelected: hasRechtsschutz,

      versicherungsgesellschaftGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.versicherungsgesellschaftGekuendigtOderAbgelehnt.value : undefined,
      versicherungsgesellschaftYearGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.versicherungsgesellschaftYearGekuendigtOderAbgelehnt.value : undefined,
      versicherungsgesellschaftPolicyNumberGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.versicherungsgesellschaftPolicyNumberGekuendigtOderAbgelehnt.value : undefined,
      versicherungsgesellschaftSparteGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.versicherungsgesellschaftSparteGekuendigtOderAbgelehnt.value : undefined,
      versicherungsgesellschaftGrundGekuendigtOderAbgelehnt: hasHaftpflicht ? skipperHaftpflicht.versicherungsgesellschaftGrundGekuendigtOderAbgelehnt.value : undefined,

      isSailAreaExplicitlyInEurope: hasRechtsschutz ? skipperRechtsschutz.isSailAreaExplicitlyInEurope.value : undefined,
      isBoatExplicitlyUsedPrivately: hasRechtsschutz ? skipperRechtsschutz.isBoatExplicitlyUsedPrivately.value : undefined,
      hasAlreadyHadLegalProtectionInsurance: hasRechtsschutz ? skipperRechtsschutz.hasAlreadyHadLegalProtectionInsurance.value : undefined,
      hasAlreadyHadDamagesOrLegalDispute: hasRechtsschutz ? skipperRechtsschutz.hasAlreadyHadDamagesOrLegalDispute.value : undefined,
      hasAlreadyHadDamagesOrLegalDisputeDescription: hasRechtsschutz ? skipperRechtsschutz.hasAlreadyHadDamagesOrLegalDisputeDescription.value : undefined,

      anmerkungCustomer: controls.customerData.controls.anmerkungCustomer.value
    } as PublicCallidusSkipperContractCreateDto;

    this.skipperStateService
      .submitSkipperOnboarding(onboardingCreateDto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.curentNavigationStep = SkipperHaftpflichtNavigationEnum.Done;
        },
        error: (error) => {
          console.error('Error submitting onboarding:', error);
        }
      });
  }

  getStepItems(): CalldiusStepItem[] {
    return this.stepItems.filter(x => x.enabled);
  }

  private initializeForm(): void {
    this.form = createSkipperForm();
  }
}

export enum SkipperHaftpflichtNavigationEnum {
  Base = 0,
  SkipperHaftpflichtDetails = 1,
  SkipperRechtsschutzDetails = 2,
  Customer = 3,
  Done = 4
}
